/**
 * Matrix Event Classification Utility
 *
 * Determines which events should affect room/channel sorting and which should not.
 * Performance: Sets are created once at module load time.
 */
import { GEMATIK_ROOM, MESSAGE_TYPES, SUPPORTED_MESSAGE_TYPES } from '../constants';
// Ephemeral events: temporary, not stored in timeline history
// These should NEVER affect room sorting
const EPHEMERAL_EVENTS = new Set([
  'm.typing',      // Typing indicators
  'm.receipt',     // Read receipts
  'm.presence',    // User online/offline status
]);

// State-only configuration events: no direct user conversation
// These should NOT affect room sorting
const STATE_ONLY_EVENTS = new Set([
  'm.room.power_levels',        // Permission/role changes
  'm.room.join_rules',          // Who can join
  'm.room.history_visibility',  // Who can see history
  'm.room.guest_access',        // Guest access settings
  'm.room.encryption',          // Encryption enabled (one-time config)
  'm.room.canonical_alias',     // Main room alias
  'm.room.aliases',             // Room alias list
  'm.room.name',           // Room name changes (metadata only)
  'm.room.topic',          // Room topic changes (metadata only)
  GEMATIK_ROOM.ROOM_NAME,   // Gematik room name changes (metadata only)
  GEMATIK_ROOM.ROOM_TOPIC,  // Gematik room topic changes (metadata only)
]);

/**
 * Determines if an event represents conversation activity for TIMESTAMP CALCULATION.
 *
 * IMPORTANT: This function is intentionally MORE INCLUSIVE than shouldCauseReorder().
 * While both functions classify events, they serve different purposes:
 *
 * - isConversationActivityEvent(): Determines what counts as "activity" for TIMESTAMP calculation
 *   (broader scope - INCLUDES metadata changes like m.room.name, m.room.avatar, m.room.topic)
 *
 * - shouldCauseReorder(): Determines what should trigger ROOM LIST REORDERING (UI update)
 *   (narrower scope - EXCLUDES metadata to prevent UI thrashing)
 *
 * DESIGN RATIONALE:
 *
 * This function uses a BLACKLIST approach (filter out unwanted events, accept everything else)
 * because:
 *
 * 1. FUTURE-PROOF: New Matrix event types automatically count as activity
 *    Example: When m.poll (MSC3381) was added, it automatically worked
 *    No code changes needed for new conversation features
 *
 * 2. TIMESTAMP ACCURACY: Metadata changes (name, avatar, topic) ARE conversation activity
 *    Example: Admin updates room topic with "Release v2.0 ships tomorrow"
 *      - This IS activity and should update lastActivity timestamp ✅
 *      - But room shouldn't jump to top (handled by shouldCauseReorder()) ✅
 *
 * 3. SAFE DEFAULT: "If it appears in the timeline, it's probably activity"
 *    Better to include too much (metadata) than exclude real conversations
 *
 * WHAT GETS INCLUDED:
 *
 * Conversation Events (Primary):
 *   ✅ m.room.message - Regular messages
 *   ✅ m.room.encrypted - Encrypted messages
 *   ✅ m.sticker - Stickers
 *   ✅ m.reaction - Message reactions
 *   ✅ m.room.redaction - Message deletions
 *   ✅ m.call.invite, m.call.answer, m.call.hangup - Calls
 *
 * Metadata Events (Secondary - THIS IS KEY):
 *   ✅ m.room.name - Room name changes
 *   ✅ m.room.avatar - Room avatar changes
 *   ✅ m.room.topic - Room topic changes
 *   ✅ m.room.create - Room creation
 *   ✅ m.room.tombstone - Room upgrade/shutdown
 *
 * Member Events (Conditional):
 *   ✅ m.room.member - User joins, leaves, kicks, bans
 *   ✅ Profile changes (displayname, avatar) also count as activity for timestamp
 *
 * WHAT GETS EXCLUDED:
 *
 *   ❌ m.typing - Typing indicators (ephemeral)
 *   ❌ m.receipt - Read receipts (ephemeral)
 *   ❌ m.presence - User presence (ephemeral)
 *   ❌ m.room.power_levels - Permission changes (config only)
 *   ❌ m.room.join_rules - Join rules (config only)
 *   ❌ m.room.encryption - Encryption settings (config only)
 *   ❌ m.local.* - Local client events
 *
 * HISTORICAL NOTE (2025-10-20):
 *
 * A proposal was made to exclude metadata events (m.room.name, m.room.avatar, m.room.topic)
 * from this function to align with shouldCauseReorder(). After devil's advocate analysis:
 *
 * - Decision: REJECT the proposal
 * - Reasoning: Metadata events ARE activity, they just shouldn't trigger UI reordering
 * - The two functions intentionally serve different purposes
 * - Tests verify this behavior (see matrixEvents.test.js lines 53-60)
 *
 * WHY METADATA IS ACTIVITY:
 *
 * Example 1: Topic announces important information
 *   - Topic changes to: "Release v2.0 ships tomorrow at 9 AM PST"
 *   - This IS conversation activity (someone communicated information)
 *   - lastActivity should update to this timestamp
 *   - But room shouldn't reorder (too disruptive)
 *
 * Example 2: Room name signals urgency
 *   - Name changes from "#backend-team" → "#P0-PRODUCTION-DOWN"
 *   - This IS activity (communicates critical status)
 *   - Timestamp should reflect this change
 *   - But reordering would be handled by subsequent messages
 *
 * USAGE:
 *
 * This function is called by getLastActivityTimestamp() to find the most recent
 * conversation activity in a room's timeline. The resulting timestamp is used for:
 *   - Sorting rooms by recent activity
 *   - Displaying "last active" times
 *   - Determining conversation recency
 *
 * RELATED FUNCTIONS:
 * - shouldCauseReorder() in src/hooks/useRoomList.js - UI reordering decisions
 * - getLastActivityTimestamp() in this file - Uses this function to find timestamps
 *
 * @param {MatrixEvent} event - Matrix event object
 * @returns {boolean} True if event represents conversation activity for timestamps
 */
export function isConversationActivityEvent(event) {
  if (!event || typeof event.getType !== 'function') {
    return false;
  }

  const eventType = event.getType();

  // Filter out ephemeral events (defensive - shouldn't be in timeline)
  if (EPHEMERAL_EVENTS.has(eventType)) {
    return false;
  }

  // Filter out state-only configuration events
  if (STATE_ONLY_EVENTS.has(eventType)) {
    return false;
  }

  // Filter out local client events
  if (eventType.startsWith('m.local.')) {
    return false;
  }

  // Special handling for m.room.member events:
  // Only membership changes (join/leave/ban/invite) count as conversation activity
  // Profile-only changes (avatar, displayname) do NOT count as activity for sorting
  if (eventType === 'm.room.member') {
    const content = event.getContent();
    const prevContent = event.getPrevContent();

    // If membership changed, it's conversation activity
    if (content.membership !== prevContent?.membership) {
      return true;
    }

    // Profile-only changes (avatar, displayname) are NOT conversation activity
    // This prevents avatar/displayname changes from affecting room sorting
    return false;
  }

  // Everything else is conversation activity:
  // - m.room.message, m.room.encrypted
  // - m.sticker, m.reaction
  // - m.call.*, m.room.redaction
  // - m.room.name, m.room.topic, m.room.avatar, m.room.create, m.room.tombstone
  return true;
}

/**
 * Gets the timestamp of the most recent conversation activity event in a timeline.
 * Iterates backwards from newest to oldest for efficiency.
 *
 * @param {Array} events - Array of Matrix events from timeline
 * @returns {number|null} Timestamp in milliseconds, or null if no activity events found
 */
export function getLastActivityTimestamp(events) {
  if (!Array.isArray(events) || events.length === 0) {
    return null;
  }

  // Iterate backwards from newest to oldest
  for (let i = events.length - 1; i >= 0; i--) {
    const event = events[i];

    if (isConversationActivityEvent(event)) {
      const timestamp = event.getTs?.();
      if (typeof timestamp === 'number' && !Number.isNaN(timestamp)) {
        // Matrix event timestamps are in milliseconds - return as-is
        return Math.floor(timestamp);
      }
    }
  }

  return null;
}

/**
 * Get the last conversation event from a room timeline.
 * This includes messages (encrypted and unencrypted), membership changes,
 * reactions, calls, etc., but excludes ephemeral events like typing indicators.
 *
 * This is the SHARED function used by both useRoomList and useChannels hooks
 * to ensure consistent event handling across the application.
 *
 * Event Types and Body Structure:
 * - 'membership': Body contains complete sentence WITH name (e.g., "Alice joined the room")
 * - 'profile_change': Body contains action WITHOUT name (e.g., "changed their profile picture")
 * - 'm.text', 'm.image', etc.: Body contains message content only
 *
 * The display component (ConversationList) adds sender prefixes based on type:
 * - membership: no prefix (name already in body)
 * - profile_change: name + space
 * - other: name + colon
 *
 * @param {Room} room - Matrix room object
 * @param {string} myUserId - Current user's Matrix ID
 * @param {function} t - Translation function
 * @returns {Object|null} Event object with type, body, senderName, timestamp, etc.
 */
export function getLastConversationEvent(room, myUserId, t) {
  // Helper to convert timestamps to unix milliseconds
  const toUnixMilliseconds = (value) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return null;
    // Matrix timestamps are already in milliseconds (13+ digits)
    // If less than 1e12, assume it's in seconds and convert to ms
    return value > 1e12 ? Math.floor(value) : Math.floor(value * 1000);
  };

  const timeline = room.getLiveTimeline();
  if (!timeline) {
    return null;
  }
  const events = timeline.getEvents();

  // Helper to get sender name
  const getSenderName = (event) => {
    const senderId = event.getSender();
    if (senderId === myUserId) return t('you');
    const sender = event.sender;
    return sender?.name || senderId;
  };

  // Helper to format message content from event content
  const formatMessageContent = (content, msgtype) => {
    if (!SUPPORTED_MESSAGE_TYPES.includes(msgtype)) {
      return null;
    }

    let body, displayContent, caption;

    switch (msgtype) {
      case 'm.text':
        body = content.body || '';
        displayContent = body;
        break;

      case 'm.image':
      case 'm.video':
      case 'm.audio':
      case 'm.file': {
        const filename = content.filename || content.body || 'Attachment';
        body = filename;
        displayContent = body;

        // If there's a caption, add it on the next line
        if (content.filename && content.body !== content.filename) {
          caption = content.body;
        }
        break;
      }

      default:
        body = content.body || '';
        displayContent = body;
    }

    return { body: displayContent, caption, msgtype };
  };

  // Helper to check if an event type is ephemeral (should not appear in conversation list)
  const isEphemeralEvent = (eventType) => {
    return ['m.typing', 'm.receipt', 'm.presence'].includes(eventType);
  };

  // Helper to check if an event is metadata-only (should not appear in conversation list)
  const isMetadataOnlyEvent = (eventType) => {
    return [
      'm.room.avatar',
      'm.room.name',
      GEMATIK_ROOM.ROOM_NAME,
      'm.room.power_levels',
      'm.room.topic',
      GEMATIK_ROOM.ROOM_TOPIC,
      'm.room.join_rules',
      'm.room.history_visibility',
      'm.room.encryption',
    ].includes(eventType);
  };

  // Iterate from newest to oldest
  for (let i = events.length - 1; i >= 0; i--) {
    const event = events[i];
    const type = event.getType();

    // Skip ephemeral events (typing, receipts, presence)
    if (isEphemeralEvent(type)) {
      continue;
    }

    // Skip metadata-only events (power levels, room name, etc.)
    if (isMetadataOnlyEvent(type)) {
      continue;
    }

    const senderId = event.getSender();
    const senderName = getSenderName(event);
    const timestamp = toUnixMilliseconds(event.getTs());

    // Handle encrypted messages
    if (type === 'm.room.encrypted') {
      // Check if message was deleted (redacted)
      if (event.isRedacted()) {
        const deletedText = t('messageDeletedBySender');
        return {
          type: 'deleted',
          body: deletedText,
          content: deletedText,
          senderId,
          senderName,
          timestamp,
        };
      }

      // Try to get decrypted content
      if (event.getClearContent && event.getClearContent()) {
        const clearContent = event.getClearContent();
        const formatted = formatMessageContent(clearContent, clearContent.msgtype);

        if (formatted) {
          return {
            type: formatted.msgtype,
            body: formatted.body,
            content: formatted.body,
            caption: formatted.caption,
            senderId,
            senderName,
            timestamp,
          };
        }
      }

      // Failed to decrypt or unsupported message type - show placeholder
      return {
        type: 'encrypted',
        body: '<encrypted>',
        content: '<encrypted>',
        senderId,
        senderName,
        timestamp,
      };
    }

    // Handle regular unencrypted messages
    if (type === 'm.room.message') {
      const content = event.getContent();

      // Check if message was deleted
      if (event.isRedacted()) {
        const deletedText = t('messageDeletedBySender');
        return {
          type: 'deleted',
          body: deletedText,
          content: deletedText,
          senderId,
          senderName,
          timestamp,
        };
      }

      const formatted = formatMessageContent(content, content.msgtype);
      if (formatted) {
        return {
          type: formatted.msgtype,
          body: formatted.body,
          content: formatted.body,
          caption: formatted.caption,
          senderId,
          senderName,
          timestamp,
        };
      }

      // Unsupported message type, continue searching
      continue;
    }

    // Handle membership events (joins/leaves, and profile changes)
    if (type === 'm.room.member') {
      const content = event.getContent();
      const prevContent = event.getPrevContent();

      // Check for membership changes first
      if (content.membership !== prevContent?.membership) {
        const targetUserId = event.getStateKey();
        const targetName = targetUserId === myUserId
          ? t('you')
          : (content.displayname || targetUserId);

        let body = '';
        switch (content.membership) {
          case 'join':
            body = targetUserId === myUserId
              ? t('youJoinedRoom')
              : `${targetName} joined the room`;
            break;
          case 'leave':
            body = targetUserId === myUserId
              ? t('youLeftRoom')
              : `${targetName} left the room`;
            break;
          case 'invite':
            body = `${targetName} was invited`;
            break;
          case 'ban':
            body = `${targetName} was banned`;
            break;
          default:
            body = `${targetName} ${content.membership}`;
        }

        return {
          type: MESSAGE_TYPES.MEMBERSHIP,
          body,
          content: body,
          senderId,
          senderName,
          timestamp,
        };
      }

      // Check for profile changes (avatar or displayname)
      const targetUserId = event.getStateKey();
      const avatarChanged = content.avatar_url !== prevContent?.avatar_url;
      const displaynameChanged = content.displayname !== prevContent?.displayname;

      if (avatarChanged || displaynameChanged) {
        // Get the person's name who made the change
        const targetName = targetUserId === myUserId
          ? t('you')
          : (content.displayname || prevContent?.displayname || targetUserId);

        // Use different translation keys for self vs others
        const isCurrentUser = targetUserId === myUserId;

        // Don't include name in body - let the display component handle it
        let body = '';
        if (avatarChanged && displaynameChanged) {
          // Both changed - show both in message
          body = isCurrentUser
            ? t('youChangedYourProfilePictureAndDisplayName')
            : t('changedTheirProfilePictureAndDisplayName');
        } else if (avatarChanged) {
          body = isCurrentUser
            ? t('youChangedYourProfilePicture')
            : t('changedTheirProfilePicture');
        } else {
          body = isCurrentUser
            ? t('youChangedYourDisplayName')
            : t('changedTheirDisplayName');
        }

        return {
          type: MESSAGE_TYPES.PROFILE_CHANGE,
          body,
          content: body,
          senderId,
          senderName: targetName,
          timestamp,
        };
      }

      // No relevant changes, skip this event
      continue;
    }

    // Handle other conversation events (reactions, calls, redactions, etc.)
    // These are less common, so we show a generic message
    const conversationEvents = [
      'm.sticker',
      'm.reaction',
      'm.room.redaction',
      'm.call.invite',
      'm.call.answer',
      'm.call.hangup',
    ];

    if (conversationEvents.includes(type)) {
      let body = '';

      switch (type) {
        case 'm.sticker':
          body = t('sentASticker');
          break;
        case 'm.reaction':
          body = t('reactedToMessage');
          break;
        case 'm.room.redaction':
          body = t('deletedAMessage');
          break;
        case 'm.call.invite':
          body = t('startedACall');
          break;
        case 'm.call.answer':
          body = t('answeredACall');
          break;
        case 'm.call.hangup':
          body = t('endedACall');
          break;
        default:
          body = 'Activity';
      }

      return {
        type,
        body,
        content: body,
        senderId,
        senderName,
        timestamp,
      };
    }
  }

  return null;
}

/**
 * Export Sets for testing purposes
 */
export const EVENT_CLASSIFICATION = {
  EPHEMERAL_EVENTS,
  STATE_ONLY_EVENTS,
};
